import React, { useState, useEffect } from 'react';
import { ArrowLeft, Star, Check, ExternalLink } from 'lucide-react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { logoUrl } from '../mockData';
import Footer from '../components/Footer';
import { AccordoLogo } from './ReteAccordi';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AccordoDettaglio() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [accordo, setAccordo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/rete-accordi`)
      .then((r) => r.json())
      .then((data) => {
        const found = (data.accordi || []).find((a) => a.slug === slug);
        setAccordo(found || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!accordo) return;
    document.title = accordo.seo_titolo || `${accordo.nome} — Rete di Accordi Assietica`;

    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta && accordo.seo_descrizione) meta.setAttribute('content', accordo.seo_descrizione);

    let canonical = document.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://assietica.it/rete-di-accordi/${accordo.slug}`);

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: accordo.nome,
      ...(accordo.logo_url ? { logo: accordo.logo_url } : {}),
      makesOffer: {
        '@type': 'Offer',
        name: `Accordo di Maggior Favore — ${accordo.nome}`,
        description: accordo.descrizione || '',
        url: accordo.url,
      },
    });
    document.head.appendChild(ld);

    return () => {
      document.title = 'Rete di Accordi — Assietica';
      if (meta && prevDesc) meta.setAttribute('content', prevDesc);
      if (createdCanonical) canonical.remove();
      ld.remove();
    };
  }, [accordo]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              data-testid="back-accordi-btn"
              onClick={() => navigate('/rete-di-accordi')}
              className="flex items-center space-x-2 text-[#2B7A9F] hover:text-[#3AAFA9] transition-colors duration-300"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Rete di Accordi</span>
            </button>
            <img src={logoUrl} alt="Assietica" className="h-12 w-auto" />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full flex-1">
        {loading ? (
          <div className="text-center py-20 text-gray-400">Caricamento...</div>
        ) : !accordo ? (
          <div className="text-center py-20">
            <p className="text-gray-600 font-medium mb-4">Accordo non trovato o non più disponibile.</p>
            <Link to="/rete-di-accordi" className="text-[#2B7A9F] underline font-medium">
              Vedi tutti gli accordi
            </Link>
          </div>
        ) : (
          <div data-testid="accordo-dettaglio" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
            {accordo.in_primo_piano && (
              <span className="inline-flex items-center gap-1 bg-[#F9D424] text-gray-900 text-xs font-bold px-3 py-1 rounded-full mb-4">
                <Star size={12} fill="currentColor" /> In primo piano
              </span>
            )}
            <div className="flex items-start gap-5 mb-6">
              <AccordoLogo accordo={accordo} size="w-20 h-20" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 leading-tight">{accordo.nome}</h1>
                <span className="inline-block mt-2 text-sm font-medium text-[#2B7A9F] bg-[#2B7A9F]/10 px-3 py-1 rounded-full">
                  {accordo.categoria}
                </span>
              </div>
            </div>

            {accordo.descrizione && (
              <p className="text-gray-700 leading-relaxed mb-6 whitespace-pre-line">{accordo.descrizione}</p>
            )}

            {accordo.vantaggi && accordo.vantaggi.length > 0 && (
              <div className="mb-8">
                <h2 className="font-bold text-gray-800 mb-3">I vantaggi dell'accordo</h2>
                <ul className="space-y-2">
                  {accordo.vantaggi.map((v, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-gray-700">
                      <Check size={17} className="text-[#5FB76E] flex-shrink-0 mt-0.5" />
                      <span>{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <a
              data-testid="accordo-dettaglio-cta"
              href={accordo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2B7A9F] hover:bg-[#256b8c] text-white font-semibold py-3.5 rounded-xl transition-colors duration-300"
            >
              Vai all'Accordo sul Portale Partner <ExternalLink size={16} />
            </a>
            <p className="text-gray-400 text-xs text-center mt-3">
              L'accesso alle condizioni dedicate avviene sul Portale Partner con il codice ricevuto dal tuo ente.
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
